/* istanbul ignore file */
import {
  KatalMonitoring,
  LogLevel,
  AppType,
  MetricDestinationSink,
} from '@amzn/katal-monitoring';
import { KatalMonitoringDriver } from '@amzn/katal-monitoring-aws-driver';
import { configure } from '@amzn/sentry-fetch';
import { MONS_ROUTE_BASE } from './constants';

const IS_BEHIND_MONS = !SERVER_MODE || SERVER_MODE === 'mons';

// Mons injects meta tags into the page head with request context.
// @see https://code.amazon.com/packages/MonsControlPlane/blobs/mainline/--/src/com/amazon/mons/control/html/SellerCentralLayoutHtmlResponseBuilder.java
const MONS_REQUEST_ID_META_TAG = 'a2z:request_id';

const getMetaContent = (name: string): string | undefined =>
  document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;

// The Mons route path for the monitoring API Gateway endpoint.
// This value is injected at deploy time by the CDK deployment lambda via KatalMonitoringUrlMons.
// Update this path if your Mons monitoring route is configured differently in Monster UI.
const MONITORING_URL_PATH = '/app-monitoring';

// Absolute monitoring endpoint for STANDALONE serving (SERVER_MODE=standalone),
// injected at deploy time (KatalMonitoringUrlStandalone). Auth is the Midway
// interceptor on the monitoring Lambda, so the browser posts directly with its
// Midway session via SentryFetch — the pattern the KatalMonitoringAWSBackend
// README prescribes for non-Mons sites. Before 2026-08 the standalone path
// silently DISABLED the driver, so no metric or error log ever left the
// browser on the deployed site.
const MONITORING_URL_STANDALONE = 'https://monitoring.beta-na.development-hiltbusinessrepor.mons-app.amazon.dev/v1/monitoring';

// Deploy-time stage label ('beta' | 'gamma' | 'prod'); unsubstituted on the
// local dev server, where monitoring stays disabled.
const STAGE_LABEL = 'beta';

/** A deploy-time placeholder that never got substituted (local dev server). */
const isUnsubstituted = (value: string) => value.startsWith('{{');

const shouldLogToConsole = () =>
  process.env.NODE_ENV !== 'test' && process.env.NODE_ENV !== 'production';

const standaloneMonitoringUrl = isUnsubstituted(MONITORING_URL_STANDALONE)
  ? undefined
  : MONITORING_URL_STANDALONE;

// Prevent monitoring from making requests when no deliverable endpoint exists.
const monitoringUrl = IS_BEHIND_MONS
  ? `${MONS_ROUTE_BASE}${MONITORING_URL_PATH}`
  : standaloneMonitoringUrl;

/**
 * Standalone transport: SentryFetch keeps the Midway session primed (MCS) and
 * sends the cookie the interceptor validates. Content type matches the
 * backend's request validator (the driver's own sendBeacon also sends
 * text/plain).
 *
 * NOTE: this is deliberately a SECOND configured SentryFetch instance —
 * ReportingApiClient owns the app's other one, but importing it here would
 * create the cycle monitoring → client → performanceMetrics → monitoring.
 *
 * A non-2xx response REJECTS (fetch resolves on HTTP errors): the driver's
 * error handler is the only client-side signal this transport has, and a 403
 * (auth misconfig) or 415 (validator mismatch) must be visible in the console
 * rather than silently repeating the monitoring-was-dark failure mode this
 * change exists to fix.
 */
const sentryFetchForMonitoring = standaloneMonitoringUrl ? configure() : undefined;
const monitoringBackendApiOverride = IS_BEHIND_MONS
  ? undefined
  : standaloneMonitoringUrl && sentryFetchForMonitoring
    ? (metricsData: string): Promise<void> =>
        sentryFetchForMonitoring(standaloneMonitoringUrl, {
          method: 'POST',
          body: metricsData,
          credentials: 'include',
          headers: { 'Content-Type': 'text/plain' },
        }).then((response) => {
          if (!response.ok) {
            throw new Error(`monitoring beacon rejected: HTTP ${response.status}`);
          }
        })
    : () => Promise.resolve(); // local dev server: no endpoint, stay silent

// site/serviceName drive the CloudWatch namespace (Katal/<site>/<serviceName>).
// Mons supplies them from request context; standalone declares them, keyed by
// the deploy-time stage label.
const metricContext = IS_BEHIND_MONS
  ? undefined
  : {
      site: isUnsubstituted(STAGE_LABEL) ? 'local' : STAGE_LABEL,
      serviceName: 'HILTBusinessReportingUIWebsite',
    };

const getMonitor = (): KatalMonitoring => {
  return new KatalMonitoring({
    monitoringDriver: new KatalMonitoringDriver({
      url: monitoringUrl,
      logToConsole: shouldLogToConsole(),
      backendApiOverride: monitoringBackendApiOverride,
    }),
    appType: IS_BEHIND_MONS ? AppType.MONS : AppType.NON_MONS,
    logThreshold: LogLevel.info,
    destinationSinks: [
      MetricDestinationSink.CLOUDWATCH,
      MetricDestinationSink.SUSHI,
    ],
    metricContext,
  });
};

const monitor: KatalMonitoring = getMonitor();
monitor.addErrorListener();

const METRIC_NAMES = {
  APP_INITIALIZED: 'App.Initialized',
  ROOT_ERROR_BOUNDARY_ENCOUNTER: 'RootErrorBoundary.Encounter',
};

const LOG_PREFIX = '[hilt_business_reporting_ui_website]';

let requestId: string | undefined;

const logError = (
  message: string,
  context?: Record<string, unknown> | undefined,
) => {
  if (!requestId) {
    // Add MONS request ID as context to aid in debugging.
    requestId = getMetaContent(MONS_REQUEST_ID_META_TAG);
  }
  monitor.error(`${LOG_PREFIX}: ${message}`, {
    ...(requestId && { requestId }),
    ...context,
  });
};

export { monitor, logError, METRIC_NAMES, LOG_PREFIX };
